import {
  Box,
  Button,
  ButtonBase,
  Drawer,
  TextField,
  Typography,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../epocus-palette";

// ─── Selectable CPT code catalogue ───────────────────────────────────────────
// All POCUS codes the engine can produce — shown as selectable options in the
// override sheet so the user picks from a known list instead of typing freetext.

export interface OverrideCptOption {
  cpt: string;
  description: string;
  category: string;
}

export const OVERRIDE_CPT_OPTIONS: OverrideCptOption[] = [
  // Cardiac
  {
    cpt: "93306",
    description: "Complete transthoracic echocardiography",
    category: "Cardiac",
  },
  {
    cpt: "93308",
    description: "Limited echocardiography follow-up study",
    category: "Cardiac",
  },
  {
    cpt: "93321",
    description: "Doppler echocardiography; spectral",
    category: "Cardiac",
  },
  {
    cpt: "93325",
    description: "Doppler echocardiography; color flow",
    category: "Cardiac",
  },
  // Abdominal / eFAST
  {
    cpt: "76705",
    description: "Abdominal limited ultrasound",
    category: "Abdominal",
  },
  {
    cpt: "76604",
    description: "Chest / lung ultrasound",
    category: "Chest",
  },
  // Renal / Aorta
  {
    cpt: "76770",
    description: "Retroperitoneal complete ultrasound",
    category: "Retroperitoneal",
  },
  {
    cpt: "76775",
    description: "Retroperitoneal limited ultrasound",
    category: "Retroperitoneal",
  },
  {
    cpt: "76706",
    description: "Aorta AAA screening ultrasound",
    category: "Retroperitoneal",
  },
  // Bladder
  {
    cpt: "51798",
    description: "Bladder residual urine measurement",
    category: "Bladder",
  },
];

const MODIFIER_OPTIONS: { label: string; value: string | null }[] = [
  { label: "None", value: null },
  { label: "-26", value: "-26" },
  { label: "-TC", value: "-TC" },
];

// ─── Public types ─────────────────────────────────────────────────────────────

export interface OverrideResult {
  cpt: string;
  modifier: string | null;
  units: number;
  description: string;
  reason: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (result: OverrideResult) => void;
  defaultCpt?: string;
  defaultModifier?: string | null;
  defaultUnits?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const BillingCptOverrideSheet: FC<Props> = ({
  open,
  onClose,
  onSubmit,
  defaultCpt = "",
  defaultModifier = null,
  defaultUnits = 1,
}) => {
  const [selectedCpt, setSelectedCpt] = useState<string>(defaultCpt);
  const [modifier, setModifier] = useState<string | null>(defaultModifier);
  const [units, setUnits] = useState<number>(defaultUnits);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedCpt(defaultCpt);
      setModifier(defaultModifier ?? null);
      setUnits(defaultUnits);
      setReason("");
    }
  }, [open, defaultCpt, defaultModifier, defaultUnits]);

  // When the user picks a different CPT, reset units to 1 since the engine's
  // unit count is code-specific (e.g. 51798 can be 2 for bladder pre+post-void).
  const handleSelectCpt = (cpt: string) => {
    setSelectedCpt(cpt);
    if (cpt !== defaultCpt) setUnits(1);
  };

  const selectedOption = OVERRIDE_CPT_OPTIONS.find(
    (o) => o.cpt === selectedCpt,
  );
  const canSubmit = selectedOption !== undefined && reason.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit || !selectedOption) return;
    onSubmit({
      cpt: selectedOption.cpt,
      modifier,
      units,
      description: selectedOption.description,
      reason: reason.trim(),
    });
  };

  // Group options by category for the list
  const categories = Array.from(
    new Set(OVERRIDE_CPT_OPTIONS.map((o) => o.category)),
  );

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <Box
        style={{
          borderRadius: "16px 16px 0 0",
          padding: "12px 24px 32px",
          backgroundColor: EPOCUS_WHITE,
          maxWidth: 680,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Handle bar */}
        <Box
          style={{
            width: 40,
            height: 4,
            backgroundColor: EPOCUS_BORDER,
            borderRadius: 2,
            margin: "0 auto 20px",
          }}
        />

        <Typography
          style={{
            fontSize: rem(20),
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            marginBottom: 4,
          }}
        >
          Override CPT Code
        </Typography>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginBottom: 20,
          }}
        >
          Select a code to replace all engine-suggested CPT codes, including any
          add-ons.
        </Typography>

        {/* Code picker */}
        <Box style={{ marginBottom: 20 }}>
          {categories.map((cat) => (
            <Box key={cat} style={{ marginBottom: 12 }}>
              <Typography
                style={{
                  fontSize: rem(10),
                  fontWeight: 700,
                  color: EPOCUS_TEXT_SECONDARY,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 6,
                }}
              >
                {cat}
              </Typography>
              {OVERRIDE_CPT_OPTIONS.filter((o) => o.category === cat).map(
                (opt) => {
                  const isSelected = selectedCpt === opt.cpt;
                  return (
                    <ButtonBase
                      key={opt.cpt}
                      aria-pressed={isSelected}
                      onClick={() => handleSelectCpt(opt.cpt)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        marginBottom: 6,
                        borderRadius: 10,
                        border: `1.5px solid ${isSelected ? EPOCUS_PRIMARY : EPOCUS_BORDER}`,
                        backgroundColor: isSelected
                          ? EPOCUS_SELECTED_BG
                          : EPOCUS_BG,
                        cursor: "pointer",
                        transition:
                          "border-color 0.15s, background-color 0.15s",
                        width: "100%",
                        textAlign: "left",
                      }}
                    >
                      <Box>
                        <Typography
                          style={{
                            fontSize: rem(14),
                            fontWeight: 700,
                            color: isSelected
                              ? EPOCUS_PRIMARY
                              : EPOCUS_TEXT_PRIMARY,
                            lineHeight: 1.3,
                          }}
                        >
                          {opt.cpt}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: rem(12),
                            color: EPOCUS_TEXT_SECONDARY,
                            marginTop: 1,
                          }}
                        >
                          {opt.description}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <Box
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: EPOCUS_PRIMARY,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginLeft: 12,
                          }}
                        >
                          <Typography
                            style={{
                              color: "#fff",
                              fontSize: rem(11),
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            ✓
                          </Typography>
                        </Box>
                      )}
                    </ButtonBase>
                  );
                },
              )}
            </Box>
          ))}
        </Box>

        {/* Modifier picker */}
        <Box style={{ marginBottom: 20 }}>
          <Typography
            style={{
              fontSize: rem(12),
              fontWeight: 600,
              color: EPOCUS_TEXT_SECONDARY,
              marginBottom: 8,
            }}
          >
            Modifier
          </Typography>
          <Box style={{ display: "flex", gap: 8 }}>
            {MODIFIER_OPTIONS.map((opt) => {
              const isActive = modifier === opt.value;
              return (
                <Button
                  key={opt.label}
                  variant="outlined"
                  size="small"
                  onClick={() => setModifier(opt.value)}
                  style={{
                    borderColor: isActive ? EPOCUS_PRIMARY : EPOCUS_BORDER,
                    backgroundColor: isActive ? EPOCUS_SELECTED_BG : undefined,
                    color: isActive ? EPOCUS_PRIMARY : EPOCUS_TEXT_SECONDARY,
                    borderRadius: 8,
                    fontWeight: isActive ? 700 : 400,
                    textTransform: "none",
                    minWidth: 56,
                    fontSize: rem(13),
                  }}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Reason */}
        <Box style={{ marginBottom: 16 }}>
          <TextField
            label="Reason for override"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            placeholder="Explain why the suggested code is being overridden…"
            InputProps={{
              style: {
                borderRadius: 8,
                fontSize: rem(16),
              },
            }}
          />
          <Typography
            style={{
              fontSize: rem(11),
              color: EPOCUS_TEXT_SECONDARY,
              marginTop: 6,
            }}
          >
            Recorded on the billing record for audit purposes.
          </Typography>
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!canSubmit}
          disableElevation
          style={{
            backgroundColor: canSubmit ? EPOCUS_PRIMARY : undefined,
            color: canSubmit ? "#fff" : undefined,
            borderRadius: 10,
            padding: "14px 0",
            fontSize: rem(16),
            fontWeight: 600,
            textTransform: "none",
            marginBottom: 8,
            minHeight: 52,
          }}
        >
          Submit Override
        </Button>

        <Button
          fullWidth
          onClick={onClose}
          style={{
            color: EPOCUS_TEXT_SECONDARY,
            fontSize: rem(14),
            textTransform: "none",
            minHeight: 44,
          }}
        >
          Cancel
        </Button>
      </Box>
    </Drawer>
  );
};
